import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { canAccessModule, isAdminLevel, ACCESS_DENIED_MESSAGES } from '../utils/permissions';

/**
 * Composant pour protéger les routes selon les permissions et modules
 */
export default function ProtectedRoute({ 
  children, 
  requiredPermission, 
  requiredRoles,
  requiredModules,
  redirectPath = '/dashboard' 
}) {
  const navigate = useNavigate();
  const { user, hasPermission, hasRole } = usePermissions();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Admin et admin_entrepot ont toujours accès
    if (isAdminLevel(user)) return;

    // Vérifier la permission si spécifiée
    if (requiredPermission && !hasPermission(requiredPermission)) {
      toast.error(ACCESS_DENIED_MESSAGES.view);
      navigate(redirectPath);
      return;
    }

    // Vérifier le rôle si spécifié (backward compat)
    if (requiredRoles && !hasRole(requiredRoles)) {
      toast.error(ACCESS_DENIED_MESSAGES.view);
      navigate(redirectPath);
      return;
    }

    // Vérifier les modules si spécifiés
    if (requiredModules && !canAccessModule(user, requiredModules)) {
      const modules = user.allowed_modules || [];
      if (modules.length === 0) {
        toast.error(ACCESS_DENIED_MESSAGES.noModule);
      } else {
        toast.error(ACCESS_DENIED_MESSAGES.view);
      }
      navigate(redirectPath);
      return;
    }
  }, [user, requiredPermission, requiredRoles, requiredModules, navigate, redirectPath, hasPermission, hasRole]);

  if (!user) return null;
  
  // Admin et admin_entrepot ont toujours accès
  if (isAdminLevel(user)) return children;
  
  if (requiredPermission && !hasPermission(requiredPermission)) return null;
  if (requiredRoles && !hasRole(requiredRoles)) return null;
  if (requiredModules && !canAccessModule(user, requiredModules)) return null;

  return children;
}
